// ============================================================
//  Analyse-Ebene (Umbau §4/§5) — abgeleitete Sichten für die Seiten.
//  Alles leitet aus der Klassifikation ab (Person → Systeme → Rollen → Werte).
// ============================================================

import type { Dataset } from "../data/generator";
import { su01Of } from "./su01";
import type { Driver, Landscape, Lever, LicenseClass, Person, SingleRole, TraceState } from "./types";
import { DEFAULT_PRICE_PER_FUE, DEFAULT_RATIO, maxClass, type RatioTable, todayIso, weight } from "./rules";
import { assignedLandscapes } from "../data/landscapes";
import { contractedFue } from "../store/contract";
import { isFueRelevantLandscape, isFueRelevantSid } from "../data/licensing";
import type { LicenseModel } from "./licensing";
import { personTraceFor } from "./trace";

export const CLASS_RANK: Record<LicenseClass, number> = {
  Developer: 4, Advanced: 3, Core: 2, SelfService: 1, NotRelevant: 0,
};

/** Umsetzungswahrscheinlichkeit aus dem Trace (§4.3). */
export function feasibility(trace: TraceState): number {
  return trace === "nie-genutzt" ? 1.0 : trace === "genutzt" ? 0.7 : 0.4;
}

const EFFORT_FACTOR: Record<string, number> = { niedrig: 0.9, mittel: 0.6, hoch: 0.3 };

/**
 * Umsetzungswahrscheinlichkeit eines Hebels (§4.3): Aufwand × Trace.
 * Aufwand niedrig 0,9 · mittel 0,6 · hoch 0,3; zusätzlich gedämpft durch den Trace
 * (genutzt = Prozessänderung, kein Trace = unbewertbar). So unterscheiden sich die Faktoren.
 */
export function leverFeasibility(effort: string, trace: TraceState): number {
  return (EFFORT_FACTOR[effort] ?? 0.6) * feasibility(trace);
}

/** §4.2 — Optimierungspotenzial je Person. */
export type Potential = "hoch" | "mittel" | "gering" | "keins";
export function optimizationPotential(p: Person): Potential {
  if (p.licenseClass === p.cleanClass) return "keins";
  const removable = p.drivers.filter((d) => d.removable);
  const driving = new Set((p.systemClasses ?? []).filter((s) => s.cls === p.licenseClass).map((s) => s.sid)).size;
  const trace = removable[0]?.trace;
  if (driving >= 2 || removable.length > 3) return "gering";
  if (p.singleSystemDriven && removable.length <= 2 && trace === "nie-genutzt") return "hoch";
  return "mittel";
}

// ---------------- Hebel-Ranking (Einzelrollen-/Wertebene, §4.3) ----------------
export interface RankedLever extends Lever {
  feasible: number; // Umsetzungswahrscheinlichkeit
  rank: number; // Δ FUE × Umsetzbarkeit
}
/**
 * Rangliste der Maßnahmen. Mit `landscapeId` werden Betroffene, Wirkung und
 * Trace-Verteilung auf die Personen dieser Landschaft eingeschränkt — und
 * Maßnahmen ohne Wirkung dort fallen heraus, statt als Nullzeile zu erscheinen.
 */
export function leverRanking(ds: Dataset, ratio: RatioTable = DEFAULT_RATIO, landscapeId?: string): RankedLever[] {
  return ds.levers
    .map((l) => {
      const feasible = leverFeasibility(l.effort, l.trace); // Aufwand × Trace, nicht konstant
      if (!landscapeId) return { ...l, feasible, rank: l.deltaFue * feasible };
      // Auf die Landschaft eingeschränkt neu rechnen — dieselbe Funktion, kleinerer Ausschnitt.
      const bd = leverBreakdown(l.id, ds, ratio, landscapeId);
      return {
        ...l,
        affected: bd.affected,
        effective: bd.effective,
        deltaFue: bd.deltaFue,
        byTrace: bd.byTrace,
        safeFue: bd.byTrace["nie-genutzt"].fue,
        feasible,
        rank: bd.deltaFue * feasible,
      };
    })
    .filter((l) => l.deltaFue > 0)
    .sort((a, b) => b.rank - a.rank);
}

// ---------------- Maßnahmen-Aufschlüsselung (Paket 1.3 / AP3.1) ----------------
export interface LeverBreakdown {
  leverId: string;
  authObject: string;
  field: string;
  value: string;
  /** Technischer Rollenname (Z_ASP_FI_VOLL_16) — das, was ein Berechtigungsteam liest. */
  roleId: string;
  /** Kurztext der Rolle; leer, wenn nicht gepflegt. */
  roleName: string;
  sid: string;
  affected: number; // Personen, die den Wert tragen
  heldBack: number; // davon: bleiben oben, weil ein zweiter Treiber greift
  effective: number; // = affected − heldBack
  fromClass: LicenseClass; // häufigste Ist-Klasse der wirksamen Personen
  toClass: LicenseClass; // häufigste Ziel-Klasse
  deltaPerPerson: number; // weight(from) − weight(to) für das häufigste Paar
  deltaFue: number; // MUSS = Σ personR sein, nicht effective × deltaPerPerson
  /**
   * Die Abstufungen nach Klassenpaar, absteigend nach Personenzahl.
   * Nicht jede Person fällt gleich weit: Wer nur diesen einen Treiber hat,
   * landet unter Umständen bei Self-Service statt bei Core. Nur diese
   * Aufschlüsselung ergibt in der Summe deltaFue — „effective × deltaPerPerson"
   * ist bei gemischten Zielklassen schlicht falsch.
   */
  transitions: {
    fromClass: LicenseClass;
    toClass: LicenseClass;
    persons: number;
    deltaPerPerson: number;
    fue: number;
  }[];
  /** true, wenn mehr als ein Klassenpaar vorkommt — dann ist die Kurzform unzulässig. */
  mixed: boolean;
  /** Strikt: nur DIESE Rolle entfernen (systemübergreifend nachgerechnet). effective/deltaFue oben = Verbund. */
  alone: { effective: number; deltaFue: number };
  /** Wer hält die Zurückgehaltenen oben — je System und Rolle, mit der Maßnahme, die es lösen würde. */
  heldBy: HeldBy[];
  /** true: allein bewirkt die Maßnahme nichts — sie greift nur zusammen mit anderen. */
  onlyInCombination: boolean;
  byTrace: Record<TraceState, { persons: number; fue: number }>;
  systems: { sid: string; persons: number }[];
  heldExamples: { personId: string; sid: string; cls: LicenseClass }[]; // max. 20
}

function mostCommon(classes: LicenseClass[]): LicenseClass {
  const c = {} as Record<LicenseClass, number>;
  for (const k of classes) c[k] = (c[k] ?? 0) + 1;
  let best: LicenseClass = "NotRelevant", n = -1;
  for (const k of Object.keys(c) as LicenseClass[]) if (c[k] > n) { best = k; n = c[k]; }
  return best;
}

/**
 * Aufschlüsselung einer Maßnahme.
 * `landscapeId` schränkt auf Personen mit Bezug zu dieser Landschaft ein —
 * dieselbe Rechnung, nur auf den Ausschnitt angewandt.
 */
// ---------------- Systemübergreifend: Was bringt eine Maßnahme ALLEIN? ----------------
// Die Klasse einer Person ist das Maximum über alle Systeme und Mandanten (R2). Eine Maßnahme in
// EINEM System senkt die Person nur, wenn kein Konto in einem anderen System oder Mandanten sie oben
// hält. Die App rechnet Maßnahmen im Verbund (Person fällt, wenn alle entfernbaren Rollen weg sind).
// Hier zusätzlich die strikte Lesart: nur diese eine Rolle entfernen — und wer dann noch hält.
interface RoleEntry { sid: string; roleId: string; step: number }
export interface RoleIndex {
  nameOf: Map<string, string>; removable: Map<string, boolean>; leverOf: Map<string, string | undefined>;
  /** Je Person: alle Rollen ihrer FUE-Konten mit Step (Entwicklerschlüssel als #DEV#, Step 10) — einmal aufgebaut. */
  personRoles: Map<string, RoleEntry[]>;
}
const ROLE_INDEX_CACHE = new WeakMap<Dataset, RoleIndex>();
export function roleIndex(ds: Dataset): RoleIndex {
  const have = ROLE_INDEX_CACHE.get(ds); if (have) return have;
  const stepOf = new Map(ds.singleRoles.map((r) => [r.id, r.forcedClass === "NotRelevant" ? 9999 : r.forcedStep]));
  const compChildren = new Map(ds.compositeRoles.map((c) => [c.id, c.singleRoleIds]));
  const accById = new Map(ds.accounts.map((a) => [a.id, a]));
  const personRoles = new Map<string, RoleEntry[]>();
  for (const p of ds.persons) {
    const entries: RoleEntry[] = [];
    for (const id of p.accountIds) {
      const a = accById.get(id); if (!a || !isFueRelevantSid(a.sid)) continue;
      if (a.isDev) entries.push({ sid: a.sid, roleId: "#DEV#", step: 10 });
      const roles = new Set<string>(a.singleRoleIds); for (const c of a.compositeRoleIds) for (const r of compChildren.get(c) ?? []) roles.add(r);
      for (const r of roles) entries.push({ sid: a.sid, roleId: r, step: stepOf.get(r) ?? 9999 });
    }
    personRoles.set(p.id, entries);
  }
  const ix: RoleIndex = {
    nameOf: new Map(ds.singleRoles.map((r) => [r.id, r.name])),
    removable: new Map(ds.singleRoles.map((r) => [r.id, !!r.removable])),
    leverOf: new Map(ds.singleRoles.map((r) => [r.id, r.leverId])),
    personRoles,
  };
  ROLE_INDEX_CACHE.set(ds, ix);
  return ix;
}
const STEP_CLASS: Record<number, LicenseClass> = { 10: "Developer", 100: "Advanced", 200: "Core", 300: "SelfService" };
/**
 * Klasse einer Person, wenn bestimmte Rollen fehlen. Liefert auch den „Halter": das Konto und die
 * Rolle, die nach dem Entfernen die Klasse trägt (bzw. #DEV#).
 */
export function classWithout(p: Person, ix: RoleIndex, drop: (roleId: string, sid: string) => boolean): { cls: LicenseClass; holder: { sid: string; roleId: string } | null } {
  let step = 9999; let holder: { sid: string; roleId: string } | null = null;
  for (const e of ix.personRoles.get(p.id) ?? []) {
    if (e.roleId !== "#DEV#" && drop(e.roleId, e.sid)) continue;
    if (e.step < step) { step = e.step; holder = { sid: e.sid, roleId: e.roleId }; }
  }
  // Untergrenze Self-Service (siehe classifyAccount): wer heute eine FUE-Klasse hat, bleibt Dialogbenutzer.
  if (step === 9999 && p.licenseClass !== "NotRelevant") step = 300;
  return { cls: STEP_CLASS[step] ?? "NotRelevant", holder };
}

/** Eine Person hinter einem Trace-Balken einer Maßnahme (G1). */
export interface LeverPersonRow {
  id: string; name?: string; funktion?: string; abteilung?: string; unit: string;
  sid: string; client: string;
  from: LicenseClass; to: LicenseClass; delta: number;
  trace: TraceState; lastLogonDays?: number;
}

/**
 * Die Personen hinter den Trace-Balken einer Maßnahme: wirksame Personen je Trace-Status
 * (dieselbe Menge wie leverBreakdown(...).byTrace) und die Zurückgehaltenen.
 */
export function leverPersons(leverId: string, ds: Dataset, ratio: RatioTable = DEFAULT_RATIO, landscapeId?: string): { byTrace: Record<TraceState, LeverPersonRow[]>; held: LeverPersonRow[] } {
  const accById = new Map(ds.accounts.map((a) => [a.id, a]));
  const byTrace: Record<TraceState, LeverPersonRow[]> = { "nie-genutzt": [], genutzt: [], "kein-trace": [] };
  const held: LeverPersonRow[] = [];
  for (const p of ds.persons) {
    if (landscapeId && !p.landscapeIds.includes(landscapeId)) continue;
    const d = p.drivers.find((x) => x.leverId === leverId);
    if (!d) continue;
    // Trace je Person = strengster Befund über alle ihre Konten (trace.ts) — wie in build.ts und im Plan.
    const trace = personTraceFor(p, leverId) ?? d.trace;
    const acc = d.accountId ? accById.get(d.accountId) : undefined;
    const su = su01Of(p, accById);
    const eff = p.leverIds.includes(leverId);
    const to = eff ? p.cleanClass : p.licenseClass;
    const rowP: LeverPersonRow = {
      id: p.id, name: p.name, funktion: su.funktion, abteilung: su.abteilung, unit: p.unit,
      sid: d.sid ?? acc?.sid ?? "—", client: acc?.client ?? "—",
      from: p.licenseClass, to, delta: weight(p.licenseClass, ratio) - weight(to, ratio),
      trace, lastLogonDays: p.lastLogonDays,
    };
    if (eff) byTrace[trace].push(rowP); else held.push(rowP);
  }
  const sorter = (a: LeverPersonRow, b: LeverPersonRow) => b.delta - a.delta || a.id.localeCompare(b.id);
  for (const t of Object.keys(byTrace) as TraceState[]) byTrace[t].sort(sorter);
  held.sort(sorter);
  return { byTrace, held };
}

export interface HeldBy { sid: string; roleId: string; roleName: string; persons: number; removable: boolean; leverId?: string }

export function leverBreakdown(
  leverId: string,
  ds: Dataset,
  ratio: RatioTable = DEFAULT_RATIO,
  landscapeId?: string,
): LeverBreakdown {
  const lv = ds.levers.find((l) => l.id === leverId);
  // Ein Halter verweist nur auf eine Maßnahme, die es auch gibt: Rollen ohne wirksame Personen
  // (weder allein noch im Verbund) erzeugen keine Maßnahme — dann steht nur die Rolle.
  const leverIds = new Set(ds.levers.map((l) => l.id));
  const leverExists = (id?: string) => (id && leverIds.has(id) ? id : undefined);
  let affected = 0, heldBack = 0, deltaFue = 0;
  const fromC: LicenseClass[] = [], toC: LicenseClass[] = [];
  const uebergaenge = new Map<string, { fromClass: LicenseClass; toClass: LicenseClass; persons: number; fue: number }>();
  const byTrace: Record<TraceState, { persons: number; fue: number }> = {
    "nie-genutzt": { persons: 0, fue: 0 }, genutzt: { persons: 0, fue: 0 }, "kein-trace": { persons: 0, fue: 0 },
  };
  const sysMap = new Map<string, number>();
  const heldExamples: { personId: string; sid: string; cls: LicenseClass }[] = [];
  const ix = roleIndex(ds);
  const roleId = lv?.roleId ?? "";
  let aloneEff = 0, aloneDelta = 0;
  const holders = new Map<string, HeldBy>();

  for (const p of ds.persons) {
    if (landscapeId && !p.landscapeIds.includes(landscapeId)) continue;
    const d = p.drivers.find((x) => x.leverId === leverId);
    if (!d) continue;
    affected++;
    // strikt: nur diese Rolle weg — fällt die Person? Wenn nicht: wer hält?
    const nur = classWithout(p, ix, (r) => r === roleId);
    if (nur.cls !== p.licenseClass) { aloneEff++; aloneDelta += weight(p.licenseClass, ratio) - weight(nur.cls, ratio); }
    else if (nur.holder) {
      const key = `${nur.holder.sid}|${nur.holder.roleId}`;
      const h = holders.get(key) ?? { sid: nur.holder.sid, roleId: nur.holder.roleId, roleName: nur.holder.roleId === "#DEV#" ? "Developer-Kennzeichen" : (ix.nameOf.get(nur.holder.roleId) ?? nur.holder.roleId), persons: 0, removable: !!ix.removable.get(nur.holder.roleId), leverId: leverExists(ix.leverOf.get(nur.holder.roleId)) };
      h.persons++; holders.set(key, h);
    }
    if (p.leverIds.includes(leverId)) {
      const r = weight(p.licenseClass, ratio) - weight(p.cleanClass, ratio);
      deltaFue += r;
      fromC.push(p.licenseClass);
      toC.push(p.cleanClass);
      const key = `${p.licenseClass}>${p.cleanClass}`;
      const u = uebergaenge.get(key) ?? { fromClass: p.licenseClass, toClass: p.cleanClass, persons: 0, fue: 0 };
      u.persons++; u.fue += r;
      uebergaenge.set(key, u);
      // Trace je Person = strengster Befund über alle ihre Konten (trace.ts) — wie in build.ts und im Plan.
      const trace = personTraceFor(p, leverId) ?? d.trace;
      byTrace[trace].persons++;
      byTrace[trace].fue += r;
      const sid = d.sid ?? p.drivingSystem ?? "—";
      sysMap.set(sid, (sysMap.get(sid) ?? 0) + 1);
    } else {
      heldBack++;
      if (heldExamples.length < 20) heldExamples.push({ personId: p.id, sid: p.drivingSystem ?? "—", cls: p.licenseClass });
    }
  }
  const fromClass = mostCommon(fromC);
  const toClass = mostCommon(toC);
  const transitions = [...uebergaenge.values()]
    .map((u) => ({ ...u, deltaPerPerson: weight(u.fromClass, ratio) - weight(u.toClass, ratio) }))
    .sort((a, b) => b.persons - a.persons);
  const systems = [...sysMap.entries()].map(([sid, persons]) => ({ sid, persons })).sort((a, b) => b.persons - a.persons);
  return {
    leverId,
    authObject: lv?.authObject ?? "", field: lv?.field ?? "", value: lv?.value ?? "", roleId, roleName: lv?.roleName ?? "", sid: lv?.sid ?? "",
    affected, heldBack, effective: affected - heldBack, fromClass, toClass,
    deltaPerPerson: weight(fromClass, ratio) - weight(toClass, ratio),
    deltaFue, byTrace, systems, heldExamples,
    transitions, mixed: transitions.length > 1,
    alone: { effective: aloneEff, deltaFue: aloneDelta },
    heldBy: [...holders.values()].sort((x, y) => y.persons - x.persons),
    onlyInCombination: aloneEff === 0 && affected - heldBack > 0,
  };
}

/**
 * Zielzustand, wenn ALLE Maßnahmen umgesetzt sind (R4): Ist − Clean über die Personen —
 * mit `landscapeId` nur über die Personen dieser Landschaft. Dieselbe Zahl steht im Cockpit
 * („Potenzial aller Maßnahmen") und in der Optimierung („Alle zusammen"); billig zu rechnen,
 * deshalb eigene Funktion statt der vollen Aufschlüsselung je Maßnahme.
 */
export function basketAllFue(ds: Dataset, ratio: RatioTable = DEFAULT_RATIO, landscapeId?: string): number {
  let ist = 0, clean = 0;
  for (const p of ds.persons) {
    if (landscapeId && !p.landscapeIds.includes(landscapeId)) continue;
    ist += weight(p.licenseClass, ratio); clean += weight(p.cleanClass, ratio);
  }
  return ist - clean;
}

export interface CrossSystemSummary {
  /** Σ der strikten Wirkungen: jede Maßnahme allein, alles andere bleibt. */
  strictSum: number;
  /** Zielzustand, wenn alle Maßnahmen umgesetzt sind (R4). */
  basketAll: number;
  /** Σ der Verbund-Wirkungen (jede Maßnahme mit allen anderen zusammen) — Personen mehrfach gezählt. */
  verbundSum: number;
  /** Maßnahmen mit Wirkung im Ausschnitt (mit Landschaft: nur die dort wirksamen). */
  levers: number;
  /** davon: allein schwächer als im Verbund. */
  dependent: number;
  /** davon: allein unwirksam. */
  onlyCombo: number;
  /** = basketAll − strictSum: fällt erst, wenn mehrere Rollen auf mehreren Systemen weg sind. */
  boundFue: number;
}

/**
 * Kennzahlen für die systemübergreifende Sicht: strikt Σ ≤ Zielzustand ≤ Verbund Σ.
 * Mit `landscapeId` auf die Personen dieser Landschaft eingeschränkt — konsistent mit
 * `leverBreakdown(…, landscapeId)` und `leverRanking(ds, ratio, landscapeId)`.
 */
export function crossSystemSummary(ds: Dataset, ratio: RatioTable = DEFAULT_RATIO, landscapeId?: string): CrossSystemSummary {
  let strictSum = 0, verbundSum = 0, dependent = 0, onlyCombo = 0, levers = 0;
  for (const lv of ds.levers) {
    const bd = leverBreakdown(lv.id, ds, ratio, landscapeId);
    // Im Landschaftsausschnitt zählen nur Maßnahmen, die dort überhaupt Personen treffen (wie leverRanking).
    if (landscapeId && bd.affected === 0) continue;
    levers++;
    strictSum += bd.alone.deltaFue; verbundSum += bd.deltaFue;
    if (bd.alone.deltaFue < bd.deltaFue - 1e-6) dependent++;
    if (bd.onlyInCombination) onlyCombo++;
  }
  const basketAll = basketAllFue(ds, ratio, landscapeId);
  return { strictSum, basketAll, verbundSum, levers, dependent, onlyCombo, boundFue: Math.max(0, basketAll - strictSum) };
}

/** Ein Personenbeispiel für „nur gemeinsam erreichbar" — aus den Daten gesucht, nicht erfunden. */
export interface ComboExample {
  personId: string;
  fromClass: LicenseClass;
  toClass: LicenseClass;
  /** Die entfernbaren Rollen auf dem obersten Step — auf mindestens zwei Systemen. */
  roles: { sid: string; roleId: string; roleName: string; leverId: string }[];
}
/**
 * Sucht eine Person mit ≥ 2 entfernbaren Rollen auf ≥ 2 Systemen, die durch KEINE davon allein
 * fällt — erst alle zusammen senken sie. Liefert null, wenn es im Ausschnitt keine solche gibt.
 */
export function comboExample(ds: Dataset, landscapeId?: string): ComboExample | null {
  const ix = roleIndex(ds);
  const leverIds = new Set(ds.levers.map((l) => l.id));
  for (const p of ds.persons) {
    if (landscapeId && !p.landscapeIds.includes(landscapeId)) continue;
    if (p.leverIds.length < 2 || p.licenseClass === p.cleanClass) continue;
    const topStep = Math.min(...p.drivers.map((d) => d.ruleStep ?? 9999));
    const seen = new Set<string>();
    const roles: ComboExample["roles"] = [];
    for (const d of p.drivers) {
      if (!d.removable || !d.leverId || d.ruleStep !== topStep || !leverIds.has(d.leverId) || !d.sid) continue;
      const key = `${d.sid}|${d.roleId}`; if (seen.has(key)) continue; seen.add(key);
      roles.push({ sid: d.sid, roleId: d.roleId, roleName: d.roleName, leverId: d.leverId });
    }
    if (new Set(roles.map((r) => r.sid)).size < 2) continue;
    // Keine der Rollen allein senkt die Person — genau das ist der Verbundfall.
    if (roles.some((r) => classWithout(p, ix, (rid) => rid === r.roleId).cls !== p.licenseClass)) continue;
    return { personId: p.id, fromClass: p.licenseClass, toClass: p.cleanClass, roles };
  }
  return null;
}

// ---------------- Rollen-Drilldown (Paket 2 / AP2) ----------------
/** Alle Rollen-IDs, die ein Konto trägt: direkte Einzelrollen + Sammelrollen + deren Einzelrollen. */
function accountRoleIds(a: { compositeRoleIds: string[]; singleRoleIds: string[] }, compById: Map<string, { singleRoleIds: string[] }>): string[] {
  const ids = new Set<string>(a.singleRoleIds);
  for (const cid of a.compositeRoleIds) {
    ids.add(cid);
    for (const sr of compById.get(cid)?.singleRoleIds ?? []) ids.add(sr);
  }
  return [...ids];
}

export function roleStats(ds: Dataset): Map<string, { holders: number; systems: number }> {
  const compById = new Map(ds.compositeRoles.map((c) => [c.id, c]));
  const persons = new Map<string, Set<string>>();
  const systems = new Map<string, Set<string>>();
  for (const a of ds.accounts) {
    if (!a.personId.startsWith("P-")) continue;
    for (const rid of accountRoleIds(a, compById)) {
      (persons.get(rid) ?? persons.set(rid, new Set()).get(rid)!).add(a.personId);
      (systems.get(rid) ?? systems.set(rid, new Set()).get(rid)!).add(a.sid);
    }
  }
  const out = new Map<string, { holders: number; systems: number }>();
  for (const rid of new Set([...persons.keys(), ...systems.keys()])) {
    out.set(rid, { holders: persons.get(rid)?.size ?? 0, systems: systems.get(rid)?.size ?? 0 });
  }
  return out;
}

export function roleHolders(roleId: string, ds: Dataset): Person[] {
  const compById = new Map(ds.compositeRoles.map((c) => [c.id, c]));
  const accById = new Map(ds.accounts.map((a) => [a.id, a]));
  const out: Person[] = [];
  for (const p of ds.persons) {
    if (p.accountIds.some((id) => { const a = accById.get(id); return a && accountRoleIds(a, compById).includes(roleId); })) out.push(p);
  }
  return out;
}

export function roleSystems(roleId: string, ds: Dataset): { sid: string; persons: number }[] {
  const compById = new Map(ds.compositeRoles.map((c) => [c.id, c]));
  const bySid = new Map<string, Set<string>>();
  for (const a of ds.accounts) {
    if (!a.personId.startsWith("P-")) continue;
    if (accountRoleIds(a, compById).includes(roleId)) (bySid.get(a.sid) ?? bySid.set(a.sid, new Set()).get(a.sid)!).add(a.personId);
  }
  return [...bySid.entries()].map(([sid, s]) => ({ sid, persons: s.size })).sort((x, y) => y.persons - x.persons);
}

// ---------------- Rollen ohne Träger / ohne Lizenzwirkung (RFP 2.2 unassigned roles, Befund C3) ----------------
export interface RoleFinding {
  id: string;
  name: string;
  sid: string;
  landscapeId: string;
  kind: "einzel" | "sammel";
  /** Klasse, die die Rolle bei einem Träger auslösen würde; null = kein Regeltreffer → keine Lizenzwirkung. */
  cls: LicenseClass | null;
  /** Objekte insgesamt (Sammelrolle: Summe der Einzelrollen). */
  objects: number;
  /** davon lizenzrelevante Werte. */
  licenseValues: number;
  /** false: System ohne FUE-Metrik (GSP, HCM) — die Rolle trägt dort keine FUE-Klasse. */
  fueRelevant: boolean;
  holders: number;
}

const STEP_OF_CLASS: Record<LicenseClass, number> = { Developer: 10, Advanced: 100, Core: 200, SelfService: 300, NotRelevant: 9999 };
const CLASS_OF_STEP: Record<number, LicenseClass> = { 10: "Developer", 100: "Advanced", 200: "Core", 300: "SelfService" };

/** Step einer Rolle (Einzel: forcedStep; Sammel: kleinster Step ihrer Einzelrollen); 9999 = keine Lizenzwirkung. */
export function roleStep(roleId: string, ds: Dataset): number {
  const sr = ds.singleRoles.find((r) => r.id === roleId);
  if (sr) return sr.forcedClass === "NotRelevant" ? 9999 : sr.forcedStep;
  const c = ds.compositeRoles.find((x) => x.id === roleId);
  if (!c) return 9999;
  const byId = new Map(ds.singleRoles.map((r) => [r.id, r]));
  let step = 9999;
  for (const id of c.singleRoleIds) { const s = byId.get(id); if (s && s.forcedClass !== "NotRelevant" && s.forcedStep < step) step = s.forcedStep; }
  return step;
}

function roleFindings(ds: Dataset, stats: Map<string, { holders: number; systems: number }>): RoleFinding[] {
  const byId = new Map(ds.singleRoles.map((r) => [r.id, r]));
  const out: RoleFinding[] = [];
  for (const s of ds.singleRoles) {
    out.push({ id: s.id, name: s.name, sid: s.sid, landscapeId: s.landscapeId, kind: "einzel", cls: s.forcedClass === "NotRelevant" ? null : s.forcedClass, objects: s.objectsTotal, licenseValues: s.authValues.length, fueRelevant: isFueRelevantSid(s.sid), holders: stats.get(s.id)?.holders ?? 0 });
  }
  for (const c of ds.compositeRoles) {
    let step = 9999, objects = 0, licenseValues = 0;
    for (const id of c.singleRoleIds) { const s = byId.get(id); if (!s) continue; objects += s.objectsTotal; licenseValues += s.authValues.length; if (s.forcedClass !== "NotRelevant" && s.forcedStep < step) step = s.forcedStep; }
    out.push({ id: c.id, name: c.name, sid: c.sid, landscapeId: c.landscapeId, kind: "sammel", cls: CLASS_OF_STEP[step] ?? null, objects, licenseValues, fueRelevant: isFueRelevantSid(c.sid), holders: stats.get(c.id)?.holders ?? 0 });
  }
  return out;
}

/** Einzel- und Sammelrollen, die NIEMANDEM zugewiesen sind — Empfehlung: löschen oder archivieren (RFP 2.2). */
export function unassignedRoles(ds: Dataset): RoleFinding[] {
  const stats = roleStats(ds);
  return roleFindings(ds, stats).filter((r) => r.holders === 0).sort((a, b) => a.sid.localeCompare(b.sid) || a.id.localeCompare(b.id));
}

/** Rollen MIT Trägern, aber ohne Regeltreffer im PCE-Regelwerk — sie erzeugen keine Lizenzklasse. */
export function rolesWithoutLicenseEffect(ds: Dataset): RoleFinding[] {
  const stats = roleStats(ds);
  return roleFindings(ds, stats).filter((r) => r.holders > 0 && r.cls === null).sort((a, b) => b.holders - a.holders || a.id.localeCompare(b.id));
}

// ---------------- Vorprüfung: Rolle vergeben / entziehen ----------------
export interface SimRow { id: string; name?: string; department?: string; unit: string; from: LicenseClass; to: LicenseClass; delta: number }
export interface SimTransition { fromClass: LicenseClass; toClass: LicenseClass; persons: number; fue: number }
export interface SimResult {
  roleId: string;
  sid: string;
  /** Klasse, die die Rolle auslöst (null = keine Lizenzwirkung, z. B. System ohne FUE-Metrik oder kein Regeltreffer). */
  roleClass: LicenseClass | null;
  persons: number;
  changed: number;
  /** Σ Gewicht(nachher) − Gewicht(vorher) bei Vergabe (≥ 0) bzw. Gewicht(vorher) − Gewicht(nachher) bei Entzug (≥ 0). */
  deltaFue: number;
  transitions: SimTransition[];
  rows: SimRow[];
}

/**
 * Vergabe simulieren: Klasse nachher = Maximum über bestehende Rollen + diese Rolle (R2), Untergrenze
 * Self-Service für eine Person, die dadurch erstmals ein FUE-Konto mit Rollen hätte. Rollen auf Systemen
 * ohne FUE-Metrik (GSP, HCM) ändern keine Klasse.
 */
export function simulateAssign(roleId: string, personIds: Iterable<string>, ds: Dataset, ratio: RatioTable = DEFAULT_RATIO): SimResult {
  const sr = ds.singleRoles.find((r) => r.id === roleId);
  const sid = sr?.sid ?? ds.compositeRoles.find((c) => c.id === roleId)?.sid ?? "";
  const fue = isFueRelevantSid(sid);
  const step = fue ? roleStep(roleId, ds) : 9999;
  const roleClass = CLASS_OF_STEP[step] ?? null;
  const byId = new Map(ds.persons.map((p) => [p.id, p]));
  const rows: SimRow[] = []; const trans = new Map<string, SimTransition>();
  let changed = 0, deltaFue = 0;
  const seen = new Set<string>();
  for (const id of personIds) {
    if (seen.has(id)) continue; seen.add(id);
    const p = byId.get(id); if (!p) continue;
    const curStep = STEP_OF_CLASS[p.licenseClass];
    // Untergrenze Self-Service: mit Rollen auf einem FUE-System ist die Person mindestens Self-Service.
    let toStep = Math.min(curStep, step);
    if (fue && toStep === 9999) toStep = 300;
    const to = CLASS_OF_STEP[toStep] ?? "NotRelevant";
    const d = weight(to, ratio) - weight(p.licenseClass, ratio);
    rows.push({ id: p.id, name: p.name, department: p.department, unit: p.unit, from: p.licenseClass, to, delta: d });
    if (to !== p.licenseClass) {
      changed++; deltaFue += d;
      const k = `${p.licenseClass}>${to}`;
      const t = trans.get(k) ?? { fromClass: p.licenseClass, toClass: to, persons: 0, fue: 0 };
      t.persons++; t.fue += d; trans.set(k, t);
    }
  }
  rows.sort((a, b) => b.delta - a.delta || a.id.localeCompare(b.id));
  return { roleId, sid, roleClass, persons: rows.length, changed, deltaFue, transitions: [...trans.values()].sort((a, b) => b.persons - a.persons), rows };
}

/** Entzug simulieren für die aktuellen Träger: Klasse ohne diese Rolle (classWithout, R2, Untergrenze Self-Service). */
export function simulateRevoke(roleId: string, ds: Dataset, ratio: RatioTable = DEFAULT_RATIO): SimResult {
  const ix = roleIndex(ds);
  const sr = ds.singleRoles.find((r) => r.id === roleId);
  const comp = ds.compositeRoles.find((c) => c.id === roleId);
  const sid = sr?.sid ?? comp?.sid ?? "";
  const fue = isFueRelevantSid(sid);
  const roleClass = fue ? (CLASS_OF_STEP[roleStep(roleId, ds)] ?? null) : null;
  const compById = new Map(ds.compositeRoles.map((c) => [c.id, c]));
  const accById = new Map(ds.accounts.map((a) => [a.id, a]));
  const rows: SimRow[] = []; const trans = new Map<string, SimTransition>();
  let changed = 0, deltaFue = 0;
  for (const p of roleHolders(roleId, ds)) {
    let after: LicenseClass = p.licenseClass;
    if (fue) {
      if (sr) after = classWithout(p, ix, (r, s) => r === roleId && s === sid).cls;
      else if (comp) {
        // Sammelrolle entziehen: ihre Einzelrollen fallen weg — außer die Person hält sie auch direkt
        // oder über eine andere Sammelrolle auf demselben System.
        const keep = new Set<string>();
        for (const aid of p.accountIds) { const a = accById.get(aid); if (!a || a.sid !== sid) continue; for (const s of a.singleRoleIds) keep.add(s); for (const c of a.compositeRoleIds) if (c !== roleId) for (const s of compById.get(c)?.singleRoleIds ?? []) keep.add(s); }
        const dropSet = new Set(comp.singleRoleIds.filter((s) => !keep.has(s)));
        after = classWithout(p, ix, (r, s) => s === sid && dropSet.has(r)).cls;
      }
    }
    const d = weight(p.licenseClass, ratio) - weight(after, ratio);
    rows.push({ id: p.id, name: p.name, department: p.department, unit: p.unit, from: p.licenseClass, to: after, delta: d });
    if (after !== p.licenseClass) {
      changed++; deltaFue += d;
      const k = `${p.licenseClass}>${after}`;
      const t = trans.get(k) ?? { fromClass: p.licenseClass, toClass: after, persons: 0, fue: 0 };
      t.persons++; t.fue += d; trans.set(k, t);
    }
  }
  rows.sort((a, b) => b.delta - a.delta || a.id.localeCompare(b.id));
  return { roleId, sid, roleClass, persons: rows.length, changed, deltaFue, transitions: [...trans.values()].sort((a, b) => b.persons - a.persons), rows };
}

// ---------------- Personen mit höchstem Potenzial (§4.2/§4.4) ----------------
export interface RankedPerson {
  person: Person;
  potential: Potential;
  deltaFue: number; // realisierbare Reduktion
  drivingSystem: string;
  fromClass: LicenseClass;
  toClass: LicenseClass;
}
export function rankedPersons(ds: Dataset, ratio: RatioTable = DEFAULT_RATIO, limit = 50, landscapeId?: string): RankedPerson[] {
  const out: RankedPerson[] = [];
  for (const p of ds.persons) {
    if (landscapeId && !p.landscapeIds.includes(landscapeId)) continue;
    if (p.leverIds.length === 0 || p.licenseClass === p.cleanClass) continue;
    out.push({
      person: p, potential: optimizationPotential(p),
      deltaFue: weight(p.licenseClass, ratio) - weight(p.cleanClass, ratio),
      drivingSystem: p.drivingSystem ?? "—", fromClass: p.licenseClass, toClass: p.cleanClass,
    });
  }
  const rankP: Record<Potential, number> = { hoch: 3, mittel: 2, gering: 1, keins: 0 };
  out.sort((a, b) => rankP[b.potential] - rankP[a.potential] || b.deltaFue - a.deltaFue);
  return out.slice(0, limit);
}

// ---------------- Landschaften: Vorgänger → RISE-Ziel (§5) ----------------
export interface LandscapeDemand {
  landscapeId: string;
  persons: number; // Personen mit Bezug (Σ > Gesamt, R1)
  fueCurrent: number; // heutiger Berechtigungsstand (Vorgängersystem)
  fueClean: number; // Bedarf nach PCE-Regelwerk, optimiert (RISE-Ziel)
  optimizable: number; // fueCurrent − fueClean
  optimizableSafe: number; // davon gefahrlos (nie-genutzt)
  /**
   * Was bringt es, NUR die Systeme dieser Landschaft zu bereinigen — alle anderen bleiben wie sie
   * sind? Meist deutlich weniger als `optimizable`, weil Konten in anderen Systemen und Mandanten
   * die Personen oben halten. Genau das ist die systemübergreifende Falle.
   */
  optimizableAlone: number;
  /** AP1: Trägt diese Landschaft überhaupt FUE bei? (Kategorie A/B) */
  fueRelevant: boolean;
}
export function landscapeDemand(ds: Dataset, ratio: RatioTable = DEFAULT_RATIO): Record<string, LandscapeDemand> {
  const acc: Record<string, LandscapeDemand> = {};
  for (const l of assignedLandscapes)
    acc[l.id] = {
      landscapeId: l.id, persons: 0, fueCurrent: 0, fueClean: 0, optimizable: 0, optimizableSafe: 0, optimizableAlone: 0,
      fueRelevant: isFueRelevantLandscape(l.id),
    };
  const ix = roleIndex(ds);
  const sidsOf = new Map(assignedLandscapes.map((l) => [l.id, new Set(l.systems)]));
  for (const p of ds.persons) {
    const cur = weight(p.licenseClass, ratio);
    const clean = weight(p.cleanClass, ratio);
    const safe = p.drivers.some((d) => d.removable && d.trace === "nie-genutzt") && p.licenseClass !== p.cleanClass ? cur - clean : 0;
    for (const lid of p.landscapeIds) {
      const a = acc[lid];
      if (!a) continue;
      a.persons++;
      // AP1: Landschaften mit eigener bzw. ohne Lizenzmetrik tragen keinen FUE-Bedarf.
      // Die Personen bleiben sichtbar — der Bedarf entsteht dort schlicht nicht.
      if (!a.fueRelevant) continue;
      a.fueCurrent += cur;
      a.fueClean += clean;
      a.optimizable += cur - clean;
      a.optimizableSafe += safe;
      if (cur > clean) { const sids = sidsOf.get(lid)!; const nur = classWithout(p, ix, (r, sid) => sids.has(sid) && !!ix.removable.get(r)); a.optimizableAlone += cur - weight(nur.cls, ratio); }
    }
  }
  return acc;
}

/**
 * AP1 — Wie viel FUE würde anfallen, wenn man Systeme mit eigener Lizenzmetrik
 * fälschlich mitzählte? Die Differenz zum tatsächlichen Bedarf. Gegenrechnung,
 * damit der Effekt der Einstufung belegbar und nicht nur behauptet ist.
 */
export function nonFueExclusion(persons: Person[], ratio: RatioTable = DEFAULT_RATIO): number {
  let summe = 0;
  for (const p of persons) {
    const alle = (p.systemClasses ?? []).map((s) => s.cls);
    if (alle.length === 0) continue;
    const hypothetisch = maxClass(alle);
    summe += weight(hypothetisch, ratio) - weight(p.licenseClass, ratio);
  }
  return summe;
}

// ---------------- Gates: Bedarfszuwachs je Stichtag (§5) ----------------
export interface GateForecast {
  landscapeId: string;
  cutover: string;
  /** AP1: false = Engine-/Sondermetrik (GSP, HCM) — Gate findet statt, bringt aber +0 FUE. */
  fueRelevant: boolean;
  /** Personen, deren Bedarf mit diesem Cutover erstmals zählt (Anker = frühester FUE-Cutover, R1). */
  persons: number;
  fueAdded: number; // Bedarf, der mit diesem Cutover hinzukommt
  avoidable: number; // davon vorher noch vermeidbar (optimierbar)
  avoidableSafe: number; // davon gefahrlos
  /** fueAdded − avoidable: was nach Optimierung von diesem System tatsächlich dazukommt. */
  target: number;
  cumulativeBefore: number; // kumulierter Bedarf vor diesem Stichtag
  cumulative: number; // kumulierter Bedarf bis einschließlich dieses Stichtags
  /** cumulativeBefore + target: Stand nach dem Gate, wenn dieses System vorher bereinigt wird. */
  cumulativeOptimized: number;
}

interface GateDemand { persons: number; fueAdded: number; avoidable: number; avoidableSafe: number }

/**
 * Bedarf je Landschaft für die Cutover-Kette — jede Person zählt EINMAL, an ihrer ANKER-Landschaft.
 * (Nicht landscapeDemand nutzen: das zählt Personen mit Mehrfach-Bezug mehrfach, R1 —
 *  korrekt für die Landschaftssicht, aber die Cutover-Kurve darf nicht doppelt zählen.)
 * `landscapes` sind die wirksamen Landschaften — mit geänderten Cutover-Daten wandert der Anker mit.
 */
function gateDemandByLandscape(ds: Dataset, ratio: RatioTable, landscapes: readonly Landscape[]): Record<string, GateDemand> {
  const cutoverOf = new Map(landscapes.map((l) => [l.id, l.cutover ?? "9999"]));
  const prim: Record<string, GateDemand> = {};
  for (const l of landscapes) prim[l.id] = { persons: 0, fueAdded: 0, avoidable: 0, avoidableSafe: 0 };
  for (const p of ds.persons) {
    // AP1: Der Bedarf wird an einer FUE-relevanten Landschaft verankert. Ist die
    // Primärlandschaft eine mit eigener Metrik (GSP/GTS, HCM), rutscht die Person
    // auf ihre erste FUE-relevante Landschaft — sonst ginge Bedarf verloren oder
    // entstünde an einem Cutover, der gar keinen FUE-Bedarf auslöst.
    // Fällig wird der Bedarf einer Person mit dem FRÜHESTEN Cutover ihrer FUE-Landschaften — nicht
    // mit dem Erstkonto. Sonst bekäme eine Landschaft, die nie „erstes" System ist (z. B. TPM als
    // Zweitsystem der Disponenten), am Gate +0 FUE, obwohl dort Personen migrieren.
    const kandidaten = p.landscapeIds.filter(isFueRelevantLandscape);
    if (!kandidaten.length) continue;
    const anchor = kandidaten.reduce((best, lid) => (cutoverOf.get(lid) ?? "9999") < (cutoverOf.get(best) ?? "9999") ? lid : best, kandidaten[0]);
    const a = prim[anchor];
    if (!a) continue;
    const cur = weight(p.licenseClass, ratio);
    const clean = weight(p.cleanClass, ratio);
    const safe = p.licenseClass !== p.cleanClass && p.drivers.some((d) => d.removable && d.trace === "nie-genutzt") ? cur - clean : 0;
    a.persons++;
    a.fueAdded += cur;
    a.avoidable += cur - clean;
    a.avoidableSafe += safe;
  }
  return prim;
}

/** Sortierung der Cutover-Kette: Datum, bei Gleichstand Reihenfolge der Datei. */
function byCutover(landscapes: readonly Landscape[]): Landscape[] {
  const pos = new Map(landscapes.map((l, i) => [l.id, i]));
  return landscapes
    .filter((l) => l.cutover)
    .sort((a, b) => (a.cutover! < b.cutover! ? -1 : a.cutover! > b.cutover! ? 1 : pos.get(a.id)! - pos.get(b.id)!));
}

/**
 * Bedarfszuwachs je Cutover — nur FUE-relevante Landschaften (AP1). Σ fueAdded = Gesamtbedarf.
 * Optionaler dritter Parameter: die wirksamen Landschaften (Standard: RFP-Plan). Ein verschobener
 * Cutover ändert nur, WANN Bedarf zählt — die Endsumme bleibt gleich.
 */
export function gateForecast(ds: Dataset, ratio: RatioTable = DEFAULT_RATIO, landscapes: readonly Landscape[] = assignedLandscapes): GateForecast[] {
  return gateChain(ds, ratio, landscapes).filter((g) => g.fueRelevant);
}

/**
 * Alle Gates der Kette (auch Engine-/Sondermetrik), mit kumuliertem Stand vor und nach dem Gate.
 * GSP/HCM stehen mit +0 darin — der Cutover findet statt, erzeugt aber keinen FUE-Bedarf.
 */
export function gateChain(ds: Dataset, ratio: RatioTable = DEFAULT_RATIO, landscapes: readonly Landscape[] = assignedLandscapes): GateForecast[] {
  const prim = gateDemandByLandscape(ds, ratio, landscapes);
  let cumulative = 0;
  return byCutover(landscapes).map((l) => {
    const fueRelevant = isFueRelevantLandscape(l.id);
    const d = fueRelevant ? prim[l.id] : { persons: 0, fueAdded: 0, avoidable: 0, avoidableSafe: 0 };
    const cumulativeBefore = cumulative;
    cumulative += d.fueAdded;
    const target = d.fueAdded - d.avoidable;
    return {
      landscapeId: l.id, cutover: l.cutover!, fueRelevant,
      persons: d.persons, fueAdded: d.fueAdded, avoidable: d.avoidable, avoidableSafe: d.avoidableSafe, target,
      cumulativeBefore, cumulative, cumulativeOptimized: cumulativeBefore + target,
    };
  });
}

/** Ein Punkt der Bedarfskurve: „heute" (Startpunkt) oder ein Gate. */
export interface GateCurvePoint {
  kind: "heute" | "gate";
  date: string;
  landscapeId?: string;
  fueRelevant: boolean;
  fueAdded: number;
  cumulative: number;
}

/**
 * Bedarfskurve entlang der Cutover-Kette. Beginnt HEUTE beim Stand der bereits vollzogenen
 * FUE-Cutover (im RFP-Plan: 0 — heute läuft nur GSP auf RISE, und GSP ist Engine). Springt
 * erst mit dem ersten Cutover einer FUE-Landschaft; Engine-/Sondermetrik-Gates stehen mit +0.
 */
export function gateCurve(ds: Dataset, ratio: RatioTable = DEFAULT_RATIO, landscapes: readonly Landscape[] = assignedLandscapes, today: string = todayIso()): GateCurvePoint[] {
  const chain = gateChain(ds, ratio, landscapes);
  const points: GateCurvePoint[] = chain.map((g) => ({ kind: "gate", date: g.cutover, landscapeId: g.landscapeId, fueRelevant: g.fueRelevant, fueAdded: g.fueAdded, cumulative: g.cumulative }));
  // „heute" liegt vor allen Gates mit späterem oder gleichem Datum — ein Cutover am Stichtag gilt als noch offen.
  const idx = points.findIndex((p) => p.date >= today);
  const at = idx === -1 ? points.length : idx;
  const standHeute = at === 0 ? 0 : points[at - 1].cumulative;
  points.splice(at, 0, { kind: "heute", date: today, fueRelevant: true, fueAdded: 0, cumulative: standHeute });
  return points;
}

/** Ein Jahr der Prognose entlang der Cutover-Kette — Stand am 31.12. */
export interface PrognoseJahr {
  jahr: number;
  /** Gates (auch Engine-/Sondermetrik), deren Cutover bis Jahresende liegt. */
  gates: number;
  /** Kumulierter Bedarf am Jahresende (letztes Gate ≤ 31.12.). */
  kumuliert: number;
  /** Σ vermeidbar über die bis dahin vollzogenen Gates. */
  vermeidbar: number;
  /** kumuliert − vermeidbar: Bedarf, wenn jedes System vor seinem Cutover bereinigt wird. */
  nachOptimierung: number;
  vertrag: number;
  /** kumuliert − Vertrag: > 0 Lücke, < 0 Puffer. */
  delta: number;
  /** nachOptimierung − Vertrag. */
  deltaOptimiert: number;
  /** EUR / Jahr bei Lücke (max(0, delta) × Preis) — 0 bei Puffer. */
  euroJahr: number;
  euroJahrOptimiert: number;
}

export const PROGNOSE_JAHRE = [2026, 2027, 2028] as const;

/**
 * Prognose je Jahr aus der Gate-Kette: Wo steht der kumulierte Bedarf am Jahresende, wo nach
 * Optimierung, wie weit über oder unter dem Vertrag — und was kostet eine Lücke pro Jahr?
 * Keine Wachstumsfaktoren: alles folgt aus den Cutover-Daten und dem Personenbestand.
 * Das letzte Jahr der Kette (2028 im RFP-Plan) = konsolidierter Gesamtbedarf.
 */
export function prognoseJeJahr(
  ds: Dataset,
  ratio: RatioTable = DEFAULT_RATIO,
  landscapes: readonly Landscape[] = assignedLandscapes,
  contracted: number = contractedFue(ratio),
  price: number = DEFAULT_PRICE_PER_FUE,
  jahre: readonly number[] = PROGNOSE_JAHRE,
): PrognoseJahr[] {
  const chain = gateChain(ds, ratio, landscapes);
  return jahre.map((jahr) => {
    const bis = chain.filter((g) => g.cutover <= `${jahr}-12-31`);
    const kumuliert = bis.length ? bis[bis.length - 1].cumulative : 0;
    const vermeidbar = bis.reduce((s, g) => s + g.avoidable, 0);
    const nachOptimierung = kumuliert - vermeidbar;
    const delta = kumuliert - contracted;
    const deltaOptimiert = nachOptimierung - contracted;
    return {
      jahr, gates: bis.length, kumuliert, vermeidbar, nachOptimierung, vertrag: contracted, delta, deltaOptimiert,
      euroJahr: Math.max(0, delta) * price, euroJahrOptimiert: Math.max(0, deltaOptimiert) * price,
    };
  });
}

// ---------------- Berechtigungs-Kette einer Person (§3, §5 Berechtigungen) ----------------
export interface ChainSingle {
  singleRoleId: string;
  singleRoleName: string;
  authObject: string;
  field: string;
  value: string;
  forcedClass: LicenseClass;
  ruleStep?: number;
  removable?: boolean;
  /** Entfernen dieser Rolle senkt die Klasse der Person tatsächlich (Maßnahme greift bei ihr). */
  effective?: boolean;
  /** Entfernbar und tragend, aber ein zweiter Treiber hält die Person oben (R3). */
  heldBack?: boolean;
  isTop: boolean; // liefert die Klasse dieses Kontos
  /** Nutzungsstatus des Kontos, das diese Rolle trägt (Trace-Zeitraum siehe data/traceMeta). */
  trace?: TraceState;
}
export interface ChainSystem {
  sid: string;
  client: string;
  accountId: string;
  cls: LicenseClass;
  engines: string[];
  isDriving: boolean; // dieses System gibt den Ausschlag für die Person
  /** AP1: Zählt dieses System FUE? Bei false ist die Klasse informativ, nicht abrechnungsrelevant. */
  fueRelevant: boolean;
  licenseModel: LicenseModel;
  trace: TraceState;
  composites: { compositeRoleId?: string; compositeRoleName?: string; singles: ChainSingle[] }[];
}
export function personChain(p: Person, ds: Dataset): ChainSystem[] {
  const compName = new Map(ds.compositeRoles.map((c) => [c.id, c.name]));
  const bySystem = new Map<string, ChainSystem>();
  for (const s of p.systemClasses ?? []) {
    bySystem.set(s.accountId, {
      sid: s.sid, client: s.client, accountId: s.accountId, cls: s.cls, engines: s.engines,
      fueRelevant: s.fueRelevant, licenseModel: s.licenseModel, trace: s.trace,
      isDriving: s.accountId === p.drivingAccountId, composites: [],
    });
  }
  // Treiber je Konto → Sammelrolle → Einzelrolle gruppieren.
  const topStepByAccount = new Map<string, number>();
  for (const d of p.drivers) if (d.accountId) topStepByAccount.set(d.accountId, Math.min(topStepByAccount.get(d.accountId) ?? 9999, d.ruleStep ?? 9999));
  for (const d of p.drivers) {
    if (!d.accountId) continue;
    const sys = bySystem.get(d.accountId);
    if (!sys) continue;
    const compId = d.compositeRoleId;
    let group = sys.composites.find((g) => g.compositeRoleId === compId);
    if (!group) { group = { compositeRoleId: compId, compositeRoleName: compId ? compName.get(compId) : undefined, singles: [] }; sys.composites.push(group); }
    const isTop = (d.ruleStep ?? 9999) === (topStepByAccount.get(d.accountId) ?? -1);
    const effective = !!d.removable && !!d.leverId && p.leverIds.includes(d.leverId);
    group.singles.push({
      singleRoleId: d.singleRoleId ?? d.roleId, singleRoleName: d.roleName, authObject: d.authObject,
      field: d.field, value: d.value, forcedClass: d.forcedClass, ruleStep: d.ruleStep, removable: d.removable,
      effective, heldBack: !!d.removable && isTop && !effective && p.licenseClass !== "SelfService" && p.licenseClass !== "NotRelevant",
      isTop, trace: d.trace,
    });
  }
  // AP1: Systeme ohne FUE-Metrik haben keine Treiber (sie erzeugen keinen Bedarf),
  // ihre Rollenkette muss aber trotzdem sichtbar sein — sonst klickt man ein System
  // auf und sieht nichts. Sie wird deshalb direkt aus dem Konto aufgebaut.
  const accById = new Map(ds.accounts.map((a) => [a.id, a]));
  const singleById = new Map(ds.singleRoles.map((r) => [r.id, r]));
  const compById = new Map(ds.compositeRoles.map((c) => [c.id, c]));
  for (const sys of bySystem.values()) {
    if (sys.fueRelevant || sys.composites.length > 0) continue;
    const acc = accById.get(sys.accountId);
    if (!acc) continue;
    const direkt: ChainSingle[] = [];
    for (const srId of acc.singleRoleIds) {
      const sr = singleById.get(srId);
      if (!sr) continue;
      direkt.push(chainSingleOf(sr, acc.trace));
    }
    if (direkt.length > 0) sys.composites.push({ singles: direkt });
    for (const cid of acc.compositeRoleIds) {
      const comp = compById.get(cid);
      if (!comp) continue;
      const singles = comp.singleRoleIds
        .map((id) => singleById.get(id))
        .filter((sr): sr is NonNullable<typeof sr> => Boolean(sr))
        .map((sr) => chainSingleOf(sr, acc.trace));
      if (singles.length > 0) sys.composites.push({ compositeRoleId: comp.id, compositeRoleName: comp.name, singles });
    }
  }

  return [...bySystem.values()].sort((a, b) => (a.isDriving === b.isDriving ? 0 : a.isDriving ? -1 : 1));
}

/** Eine Einzelrolle als Kettenzeile — ohne Treiberbezug, für Systeme ohne FUE-Metrik. */
function chainSingleOf(sr: SingleRole, trace?: TraceState): ChainSingle {
  const av = sr.authValues[0];
  return {
    trace,
    singleRoleId: sr.id,
    singleRoleName: sr.name,
    authObject: av?.obj ?? "—",
    field: av?.field ?? "",
    value: av?.val ?? "",
    forcedClass: sr.forcedClass,
    ruleStep: sr.forcedStep,
    removable: sr.removable,
    isTop: false,
  };
}

/** Der ausschlaggebende Treiber einer Person (höchste Klasse, im drivingSystem). */
export function drivingDriver(p: Person): Driver | undefined {
  const top = p.drivers.filter((d) => d.sid === p.drivingSystem);
  return top.sort((a, b) => (a.ruleStep ?? 9999) - (b.ruleStep ?? 9999))[0] ?? p.drivers[0];
}

// ---------------- Mehrere Mandanten im selben System (R1 auf Systemebene) ----------------
export interface MultiClientStat { sid: string; persons: number; accounts: number; maxClients: number }
/**
 * Personen, die im SELBEN System Konten in mehreren Mandanten haben. Sie sind der
 * Alltagsfall bei 110 Mandanten — und der Beweis, dass R1 auch innerhalb eines
 * Systems gilt: viele Konten, eine Person, ein FUE.
 */
export function multiClientStats(ds: Dataset): { bySid: Record<string, MultiClientStat>; personsTotal: number; maxClients: number } {
  const bySid: Record<string, MultiClientStat> = {};
  const seen = new Set<string>();
  let maxClients = 0;
  for (const p of ds.persons) {
    const perSid = new Map<string, number>();
    for (const sc of p.systemClasses ?? []) perSid.set(sc.sid, (perSid.get(sc.sid) ?? 0) + 1);
    for (const [sid, n] of perSid) {
      if (n < 2) continue;
      const e = bySid[sid] ?? (bySid[sid] = { sid, persons: 0, accounts: 0, maxClients: 0 });
      e.persons++; e.accounts += n; e.maxClients = Math.max(e.maxClients, n);
      maxClients = Math.max(maxClients, n);
      seen.add(p.id);
    }
  }
  return { bySid, personsTotal: seen.size, maxClients };
}

/** Hat diese Person im selben System mehr als ein Konto? */
export function hasMultiClient(p: Person): boolean {
  const sids = (p.systemClasses ?? []).map((s) => s.sid);
  return new Set(sids).size < sids.length;
}
